import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronLeft, GraduationCap } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import ScrollReveal from '../../components/common/ScrollReveal';

type TeacherSubject = {
  id: string;
  name: string;
  gradeName?: string;
  gradeId?: string;
  courseCount?: number;
};

type TeacherGrade = {
  gradeName: string;
  gradeId: string;
  subjects: TeacherSubject[];
  courseCount: number;
};

const GRADE_ORDER = [
  'الأول الإعدادي',
  'أولى إعدادي',
  'الثاني الإعدادي',
  'ثانية إعدادي',
  'الثالث الإعدادي',
  'ثالثة إعدادي',
  'الأول الثانوي',
  'أولى ثانوي',
  'الثاني الثانوي',
  'ثانية ثانوي',
  'الثالث الثانوي',
  'ثالثة ثانوي',
  'تالتة ثانوي',
];

function getGradeValue(gradeName?: string): string {
  if (!gradeName) return 'secondary1';
  if (gradeName.includes('الأول الثانوي') || gradeName.includes('أولى ثانوي')) return 'secondary1';
  if (gradeName.includes('الثاني الثانوي') || gradeName.includes('ثانية ثانوي')) return 'secondary2';
  if (gradeName.includes('الثالث الثانوي') || gradeName.includes('ثالثة ثانوي') || gradeName.includes('تالتة ثانوي')) {
    return 'secondary3';
  }
  if (gradeName.includes('الأول الإعدادي') || gradeName.includes('أولى إعدادي')) return 'prep1';
  if (gradeName.includes('الثاني الإعدادي') || gradeName.includes('ثانية إعدادي')) return 'prep2';
  if (gradeName.includes('الثالث الإعدادي') || gradeName.includes('ثالثة إعدادي')) return 'prep3';
  return 'secondary1';
}

function toGradeStateKey(gradeValue: string): string {
  const map: Record<string, string> = {
    secondary1: 'Secondary1',
    secondary2: 'Secondary2',
    secondary3: 'Secondary3',
    prep1: 'Prep1',
    prep2: 'Prep2',
    prep3: 'Prep3',
  };
  return map[gradeValue] ?? 'Secondary1';
}

function gradeSortIndex(gradeName: string): number {
  const idx = GRADE_ORDER.findIndex((label) => gradeName.includes(label) || label.includes(gradeName));
  return idx === -1 ? 999 : idx;
}

function groupSubjectsByGrade(subjects: TeacherSubject[]): TeacherGrade[] {
  const map = new Map<string, TeacherGrade>();

  subjects.forEach((subject) => {
    const gradeId = subject.gradeId || subject.gradeName || 'unknown';
    const gradeName = subject.gradeName || 'مرحلة غير محددة';
    const existing = map.get(gradeId);

    if (existing) {
      existing.subjects.push(subject);
      existing.courseCount += subject.courseCount ?? 0;
    } else {
      map.set(gradeId, {
        gradeId,
        gradeName,
        subjects: [subject],
        courseCount: subject.courseCount ?? 0,
      });
    }
  });

  return Array.from(map.values()).sort(
    (a, b) => gradeSortIndex(a.gradeName) - gradeSortIndex(b.gradeName)
  );
}

const PortalTeacherPage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const [teacherName, setTeacherName] = useState('المدرس');
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(true);

  const grades = useMemo(() => groupSubjectsByGrade(subjects), [subjects]);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!teacherId) return;
      try {
        setLoading(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{ id: string; fullName: string; active: boolean }>;
        }>(`${BASE_URL}api/Teacher`, {
          params: { active: true },
          headers: { ...getTenantHeaders() },
        });

        const found = response.data?.data?.find((t) => t.id === teacherId);
        if (found) {
          setTeacherName(found.fullName);
        }
      } catch {
        // keep default name
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [teacherId]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!teacherId) return;
      try {
        setLoadingGrades(true);
        const response = await axios.get<{
          success: boolean;
          data?: Array<{
            id: string;
            name: string;
            gradeName?: string;
            gradeId?: string;
            courseCount?: number;
          }>;
        }>(`${BASE_URL}api/Teacher/${teacherId}/subjects`, {
          headers: { ...getTenantHeaders() },
        });

        if (response.data?.success && response.data.data) {
          setSubjects(
            response.data.data.map((s) => ({
              id: s.id,
              name: s.name,
              gradeName: s.gradeName,
              gradeId: s.gradeId,
              courseCount: s.courseCount,
            }))
          );
        } else {
          setSubjects([]);
        }
      } catch {
        setSubjects([]);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchSubjects();
  }, [teacherId]);

  const handleGradeClick = (grade: TeacherGrade) => {
    const gradeValue = getGradeValue(grade.gradeName);
    navigate(`/courses/grade/${gradeValue}`, {
      state: {
        grade: toGradeStateKey(gradeValue),
        teacherId,
        subjects: grade.subjects,
        fromStudentPortal: true,
      },
    });
  };

  return (
    <div className="pb-8" dir="rtl">
      <div className="border-b border-slate-100 bg-white p-4 sm:p-6 lg:p-8">
        <button
          type="button"
          onClick={() => navigate('/student-portal')}
          className="mb-5 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-[#001848] hover:text-gold-600"
        >
          <ArrowRight className="h-5 w-5" />
          العودة للحصص والكورسات
        </button>

        <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 sm:text-sm">
          <GraduationCap className="h-3.5 w-3.5" />
          {loading ? 'جاري التحميل...' : teacherName}
        </div>
        <h1 className="text-xl font-bold text-[#001848] font-cairo-bold sm:text-2xl lg:text-3xl">
          اختَر مرحلتك الدراسية
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500 sm:text-base">
          اضغط على المرحلة لعرض الكورسات
        </p>
      </div>

      <section className="bg-[#F4F6FA] py-6 sm:py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loadingGrades ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white"
                />
              ))}
            </div>
          ) : grades.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
              {grades.map((grade, index) => (
                <ScrollReveal key={grade.gradeId || index} variant="up" delay={index * 70}>
                  <button
                    type="button"
                    onClick={() => handleGradeClick(grade)}
                    className="group relative flex min-h-[7.5rem] w-full flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-right shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold-400/50 hover:shadow-lg hover:shadow-[#001848]/10 sm:min-h-[8rem] sm:p-6"
                  >
                    <span className="absolute inset-y-0 end-0 w-1 bg-gradient-to-b from-gold-400 to-[#001848] opacity-80 transition-all group-hover:w-1.5" />
                    <div className="flex items-start justify-between gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#001848] text-white transition-colors group-hover:bg-gold-500">
                        <GraduationCap className="h-5 w-5" />
                      </span>
                      <ChevronLeft className="mt-1 h-5 w-5 text-slate-300 transition-colors group-hover:text-[#001848]" />
                    </div>
                    <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-[#001848] font-cairo-bold sm:text-lg">
                      {grade.gradeName}
                    </h3>
                  </button>
                </ScrollReveal>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-10 text-center shadow-sm">
              <GraduationCap className="mx-auto mb-3 h-12 w-12 text-slate-300" />
              <p className="text-sm text-slate-600 sm:text-base">
                لا توجد مراحل دراسية متاحة لهذا المدرس حالياً
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PortalTeacherPage;
